import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faMapPin, faSignOutAlt, faUserCircle, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

// Sub-modules
import AdminSidebar from './AdminSidebar';
import AdminStats from './AdminStats';
import TurnTypeABM from './TurnTypeABM';
import UserABM from './UserABM';
import ScreenParams from './ScreenParams';
import EmpresaABM from './EmpresaABM';
import SucursalABM from './SucursalABM';
import InstallationManager from './InstallationManager';
import MySubscription from './MySubscription';
import SpecialtyABM from './SpecialtyABM';
import SpecialistABM from './SpecialistABM';

import ConnectionManager from '../SuperAdmin/MenuBuilder/ConnectionManager';
import QueryBuilderPanel from '../SuperAdmin/MenuBuilder/QueryBuilderPanel';
import MenuBuilderPanel from '../SuperAdmin/MenuBuilder/MenuBuilderPanel';
import DownloadSpool from '../SuperAdmin/DownloadSpool';

// Styles & Tools
import './TurneroAdmin.css';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';
import DinamicRoute from '../../Core/DinamicRoute';

/**
 * AdminPanel - Premium Dashboard Overhaul
 * Features Collapsible Sidebar, Topbar with Glass Selectors, and Stats View.
 */
const AdminPanel = ({ ipserv }) => {
    const user = JSON.parse(localStorage.getItem('dm_admin') || '{}');
    const isStaff = user.AdminRole === 'STAFF';
    const [activeTab, setActiveTab] = useState(isStaff ? '' : 'dashboard');
    const [isCollapsed, setIsCollapsed] = useState(false);
    
    // State for tenant selection
    const [companies, setCompanies] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [branches, setBranches] = useState([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
        if (isStaff && user.EmpresaId) return String(user.EmpresaId);
        return localStorage.getItem('dm_selected_company') || '';
    });
    
    const [selectedBranchId, setSelectedBranchId] = useState(() => {
        if (isStaff && user.SucursalId) return String(user.SucursalId);
        return localStorage.getItem('dm_selected_branch') || '';
    });
    const [menuGroups, setMenuGroups] = useState([]);
    
    // Live Clock State
    const [currentTime, setCurrentTime] = useState(new Date());

    // Expiration Logic
    const expiryDate = user.TrialExpiry ? new Date(user.TrialExpiry) : null;
    const daysRemaining = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : 999;
    const isExpiringSoon = daysRemaining <= 7 && daysRemaining >= 0;
    const isExpired = daysRemaining < 0;

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // If staff and no active tab, pick first dynamic module
    useEffect(() => {
        if (isStaff && !activeTab && menuGroups.length > 0) {
            if (menuGroups[0].items && menuGroups[0].items.length > 0) {
                setActiveTab(menuGroups[0].items[0].id);
            }
        }
    }, [isStaff, activeTab, menuGroups]);


    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

    // Load companies and branches
    const loadTenantData = () => {
        request.Get(API_BASE + 'platform/companies', {}, r => {
            if (r.success) setCompanies(r.data || []);
        });

        request.Get(API_BASE + 'platform/branches', {}, r => {
            if (r.success) setAllBranches(r.data || []);
        });

        // Fetch dynamic menu
        request.Get(API_BASE + 'platform/menu', {}, r => {
            if (r.success) setMenuGroups(r.data || []);
        });
    };

    useEffect(() => {
        loadTenantData();
    }, [API_BASE]);


    // Multi-tenant filtering logic
    useEffect(() => {
        if (selectedCompanyId && allBranches.length > 0) {
            const filteredBranches = allBranches.filter(b => String(b.EmpresaId) === String(selectedCompanyId));
            setBranches(filteredBranches);
            
            if (!selectedBranchId || !filteredBranches.find(b => String(b.SucursalId) === String(selectedBranchId))) {
                if (filteredBranches.length > 0) {
                    handleBranchChange(filteredBranches[0].SucursalId);
                } else {
                    handleBranchChange('');
                }
            }
        } else {
            setBranches([]);
        }
    }, [selectedCompanyId, allBranches]);

    const handleCompanyChange = (id) => {
        setSelectedCompanyId(id);
        localStorage.setItem('dm_selected_company', id);
    };

    const handleBranchChange = (id) => {
        setSelectedBranchId(id);
        localStorage.setItem('dm_selected_branch', id);
    };

    useEffect(() => {
        if (activeTab) localStorage.setItem('dm_active_tab', activeTab);
    }, [activeTab]);

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    // Helper to render active module
    const renderContent = () => {
        const props = { 
            ipserv, 
            branch_id: selectedBranchId, 
            hideHeader: true,
            onRefresh: loadTenantData
        };
        
        // Static mapping for existing components
        const componentMap = {
            'dashboard': AdminStats,
            'empresa': EmpresaABM,
            'sucursales': SucursalABM,
            'types': TurnTypeABM,
            'users': UserABM,
            'params': ScreenParams,
            'installation': InstallationManager,
            'subscription': MySubscription,
            'specialties': SpecialtyABM,
            'specialists': SpecialistABM,
            'connections': ConnectionManager,
            'querybuilder': QueryBuilderPanel,
            'menubuilder': MenuBuilderPanel
        };

        const Component = componentMap[activeTab];
        
        if (Component) return <Component {...props} />;

        // Handle dynamic modules (e.g. ones configured in Menu Builder)
        const findDynamicModule = (list) => {
            if (!list) return null;
            for (const item of list) {
                const itemId = item.id || item.ModuleKey || String(item.IMenuId);
                if (itemId === activeTab) return item;
                
                const foundInItems = findDynamicModule(item.items);
                if (foundInItems) return foundInItems;
                
                const foundInMenus = findDynamicModule(item.menus);
                if (foundInMenus) return foundInMenus;
            }
            return null;
        };
        
        const dynamicMod = findDynamicModule(menuGroups);
        if (dynamicMod) {
            let dynParams = {};
            try { 
                dynParams = dynamicMod.autQParams 
                    ? JSON.parse(dynamicMod.autQParams) 
                    : (dynamicMod.IMenuParams ? JSON.parse(dynamicMod.IMenuParams) : {}); 
            } catch (e) { }
            
            return (
                <DinamicRoute
                    key={activeTab}
                    QueId={dynamicMod.queryId || dynamicMod.IMenuAutQId}
                    Title={dynamicMod.autQTitle || dynamicMod.label || dynamicMod.IMenuNom}
                    params={dynParams}
                    ipserv={ipserv}
                />
            );
        }

        return <div className="p-5 text-center text-muted">Módulo en desarrollo o dinámico: {activeTab}</div>;
    };

    return (
        <div className="admin-layout">
            <AdminSidebar 
                activeTab={activeTab} 
                onTabChange={setActiveTab} 
                isCollapsed={isCollapsed}
                toggleCollapse={() => setIsCollapsed(!isCollapsed)}
                menuGroups={menuGroups}
            />

            <main className="admin-main">
                <header className="admin-topbar">
                    <div className="topbar-selectors">
                        {isStaff ? (
                            <div className="d-flex align-items-center gap-3">
                                <div className="premium-location-badge">
                                    <FontAwesomeIcon icon={faBuilding} className="text-primary mr-2" />
                                    <span className="location-label">Empresa:</span>
                                    <span className="location-value">
                                        {companies.find(c => String(c.EmpresaId) === String(selectedCompanyId))?.EmpresaNombre || 'Cargando...'}
                                    </span>
                                </div>
                                <div className="location-separator"></div>
                                <div className="premium-location-badge">
                                    <FontAwesomeIcon icon={faMapPin} className="text-warning mr-2" />
                                    <span className="location-label">Sucursal:</span>
                                    <span className="location-value">
                                        {allBranches.find(b => String(b.SucursalId) === String(selectedBranchId))?.SucursalNombre || 'Cargando...'}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="glass-selector">
                                    <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                                    <select 
                                        value={selectedCompanyId} 
                                        onChange={(e) => handleCompanyChange(e.target.value)}
                                    >
                                        <option value="" disabled>Seleccionar Empresa</option>
                                        {companies.map(c => <option key={c.EmpresaId} value={c.EmpresaId}>{c.EmpresaNombre}</option>)}
                                    </select>
                                </div>

                                <div className="glass-selector">
                                    <FontAwesomeIcon icon={faMapPin} className="text-warning" />
                                    <select 
                                        value={selectedBranchId} 
                                        onChange={(e) => handleBranchChange(e.target.value)}
                                    >
                                        <option value="" disabled>Seleccionar Sucursal</option>
                                        {branches.map(b => <option key={b.SucursalId} value={b.SucursalId}>{b.SucursalNombre}</option>)}
                                    </select>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-4">
                        <div className="d-none d-xl-flex flex-column align-items-end text-end mr-3">
                            <span className="small font-weight-bold" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
                                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="tiny text-muted">
                                {currentTime.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' })}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-3 pr-3 border-right border-light border-opacity-10">
                            <div className="position-relative">
                                <FontAwesomeIcon icon={faUserCircle} size="2x" className="text-muted opacity-50" />
                                <span className="status-indicator online"></span>
                            </div>
                            <div className="d-flex flex-column">
                                <span className="small font-weight-bold m-0 line-height-1">
                                    {JSON.parse(localStorage.getItem('dm_admin') || '{}').AdminNombre || 'Usuario'}
                                </span>
                                <span className="tiny text-success font-weight-bold" style={{ fontSize: '0.65rem' }}>
                                    {JSON.parse(localStorage.getItem('dm_admin') || '{}').AdminRole === 'STAFF' ? 'STAFF' : 'ADMINISTRADOR'} EN LÍNEA
                                </span>
                            </div>
                        </div>

                        <button className="premium-button px-3 py-2" onClick={handleLogout} style={{ fontSize: '0.8rem', background: '#ef4444' }}>
                            <FontAwesomeIcon icon={faSignOutAlt} /> Salir
                        </button>
                    </div>
                </header>

                <div className="admin-viewport">
                    {/* Expiration Warning Banner */}
                    {(isExpiringSoon || isExpired) && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            style={{ 
                                background: isExpired ? 'rgba(239, 68, 68, 0.05)' : 'rgba(245, 158, 11, 0.05)',
                                border: `1px solid ${isExpired ? '#ef4444' : '#f59e0b'}`,
                                borderRadius: 12, padding: '1rem', marginBottom: '1.5rem',
                                display: 'flex', alignItems: 'center', gap: '1rem',
                                color: isExpired ? '#ef4444' : '#f59e0b'
                            }}
                        >
                            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.5rem' }} />
                            <div>
                                <h6 style={{ margin: 0, fontWeight: 700 }}>
                                    {isExpired ? 'Suscripción Vencida' : 'Suscripción por Vencer'}
                                </h6>
                                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.8 }}>
                                    {isExpired 
                                        ? 'Tu licencia ha expirado. Algunas funciones de creación han sido deshabilitadas.' 
                                        : `Tu licencia de prueba vence en ${daysRemaining} ${daysRemaining === 1 ? 'día' : 'días'}. Contacta a soporte para renovar.`}
                                </p>
                            </div>
                        </motion.div>
                    )}

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            {renderContent()}
                        </motion.div>
                    </AnimatePresence>

                    {!selectedBranchId && !['dashboard', 'empresa', 'sucursales'].includes(activeTab) && (
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }}
                            className="auth-card mt-4 p-3 text-center border-warning border-opacity-25"
                        >
                            <p className="text-warning m-0 small">
                                <FontAwesomeIcon icon={faMapPin} className="mr-2" />
                                Por favor selecciona una sucursal para gestionar sus datos.
                            </p>
                        </motion.div>
                    )}
                </div>
            </main>
            <DownloadSpool />
        </div>
    );
};

export default AdminPanel;
